import type { Timestamp } from 'firebase/firestore';

/**
 * Prayer types/categories
 */
export type PrayerType = 'healing' | 'memory' | 'dedication';

/**
 * Status of a pending prayer submission
 */
export type PendingPrayerStatus = 'pending' | 'approved' | 'rejected';

/**
 * An approved prayer that is publicly visible
 */
export interface Prayer {
  id: string;
  name: string; // English name
  hebrewName?: string; // Hebrew name (optional)
  note?: string; // Optional note/dedication message
  type: PrayerType;
  addedBy: string; // UID of submitter
  addedByName?: string; // Display name of submitter
  createdAt: Timestamp | null;
  // Community posting fields
  postedToCommunity?: boolean;
  communityMessageId?: string; // Reference to the community message
  postedAt?: Timestamp;
  postedBy?: string; // UID of who posted to community
  // Donation template linking
  donationTemplateId?: string; // Reference to a donation template
  donationTemplateName?: string; // Cached template name for display
  donationLinkedAt?: Timestamp; // When the template was linked
  donationLinkedBy?: string; // UID of who linked the template
  // Pinned prayer fields (1 per type, set by leaders/admins)
  pinned?: boolean;
  pinnedAt?: Timestamp;
  pinnedBy?: string; // UID of admin/leader who pinned
}

/**
 * A prayer submission that is pending moderator approval
 */
export interface PendingPrayer {
  id: string;
  name: string; // English name
  hebrewName?: string; // Hebrew name (optional)
  note?: string; // Optional note/dedication message
  type: PrayerType;
  addedBy: string; // UID of submitter
  addedByName?: string; // Display name of submitter
  status: PendingPrayerStatus;
  submittedAt: Timestamp;
  reviewedAt?: Timestamp;
  reviewedBy?: string; // UID of reviewer
  reviewedByName?: string; // Display name of reviewer
  rejectionReason?: string; // Optional reason if rejected
}

/**
 * Input for submitting a new prayer request
 */
export interface PrayerSubmissionInput {
  name: string;
  hebrewName?: string;
  note?: string;
  type: PrayerType;
}

/**
 * Configuration for prayer categories
 */
export interface PrayerCategoryConfig {
  tabLabel: string;
  title: string;
  hebrewTitle: string;
  desc: string;
  gradient: string;
  iconColor: string;
  iconColorHex: string;
  borderColor: string;
  iconBg: string;
  dotColor: string;
}

/**
 * Category configuration for all prayer types
 * Uses purple tint system from the shabbat palette instead of red/orange/yellow
 */
export const PRAYER_CATEGORY_CONFIG: Record<PrayerType, PrayerCategoryConfig> = {
  healing: {
    tabLabel: 'Refuah Shleimah',
    title: 'Refuah Shleimah',
    hebrewTitle: '\u05E8\u05E4\u05D5\u05D0\u05D4 \u05E9\u05DC\u05DE\u05D4',
    desc: 'Prayers for those in need of healing.',
    gradient: 'from-[#a04d9a] to-[#8e4585]',
    iconColor: 'text-[#a04d9a]',
    iconColorHex: '#a04d9a',
    borderColor: 'border-[#e5bce5]',
    iconBg: 'bg-[#f5ddf5]',
    dotColor: '#a04d9a',
  },
  memory: {
    tabLabel: 'In Memoriam',
    title: 'In Memoriam',
    hebrewTitle: '\u05DC\u05D6\u05DB\u05E8',
    desc: 'Prayers in memory of those whom we have lost.',
    gradient: 'from-[#7a4275] to-[#63345e]',
    iconColor: 'text-[#7a4275]',
    iconColorHex: '#7a4275',
    borderColor: 'border-[#e5bce5]',
    iconBg: 'bg-[#eedcee]',
    dotColor: '#7a4275',
  },
  dedication: {
    tabLabel: 'Study Dedications',
    title: 'Study Dedications',
    hebrewTitle: '\u05D4\u05E7\u05D3\u05E9\u05D4',
    desc: 'Learning dedicated in honor or celebration.',
    gradient: 'from-[#d4a832] to-[#b89430]',
    iconColor: 'text-[#b89430]',
    iconColorHex: '#b89430',
    borderColor: 'border-[#e5bce5]',
    iconBg: 'bg-[#fdf5e0]',
    dotColor: '#b89430',
  },
};
